import { Component, Input, OnInit } from '@angular/core';
import { appear } from '../../animations/appear';
import { ChartConfiguration, ChartType, LegendItem } from 'chart.js';
import { BehaviorSubject, filter, takeUntil } from 'rxjs';
import { ReviewDocumentState, ReviewDocumentStatusCategory, ReviewDocumentStatusType } from '@models/ui/case-analytics-state/review-document-state.model';
import { cloneDeep } from 'lodash-es';
import { ThemeService as ChartThemeService } from 'ng2-charts';
import { LexbeThemeService as LexbeThemeService } from '@services/lexbe-theme.service';
import { CaseAnalyticsItemBaseComponent } from '../case-analytics-item-base.component';

@Component({
    selector: 'lex-case-analytics-review-documents',
    templateUrl: './case-analytics-review-documents.component.html',
    styleUrl: './case-analytics-review-documents.component.scss',
    animations: [
        appear
    ],
    standalone: false
})
export class CaseAnalyticsReviewDocumentsComponent extends CaseAnalyticsItemBaseComponent implements OnInit {
  @Input() set reviewDocumentState(state: ReviewDocumentState) {
    this.reviewDocumentState$.next(state);
  }

  public statusView: ReviewDocumentStatusView = null;
  public shouldShowLoader: boolean = true;

  public doughnutChartOptions: ChartConfiguration['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    parsing: {
      key: 'nested.doughnutDisplayAmount'
    },
    datasets: {
      doughnut: {
        borderAlign: 'inner',
      }
    },
    scales: null,
    onHover: this.handleHover.bind(this),
    plugins: {
      legend: {
        position: 'right',
        labels: {
          usePointStyle: true,
          pointStyle: 'circle',
          boxWidth: 8,
          boxHeight: 8,
          generateLabels: (chart) => {
            const updatedLabels = [];
            const activeLabelIndices = chart.getActiveElements();
            const labels = chart.data.labels;
            labels.forEach((label) => {
              const view = this.doughnutHelper.getViewDataFromStatus(label as ReviewDocumentStatusType);
              const index = this.doughnutHelper.getIndexFromStatus(label as ReviewDocumentStatusType);
              const dataset = chart.data.datasets[view.dbSet];

              let fillStyle = dataset.backgroundColor[index];
              let fontColor = this.chartFontCurrentColors.normal;
              let strokeStyle = 'transparent';

              if (activeLabelIndices.findIndex(active => active.datasetIndex === view.dbSet && active.index === index) > -1) {
                fillStyle = dataset.hoverBackgroundColor[index];
                strokeStyle = dataset.borderColor[index];
                fontColor = this.chartFontCurrentColors.hover;
              }

              updatedLabels.push({
                text: label,
                fontColor: fontColor,
                fillStyle: fillStyle,
                strokeStyle: strokeStyle,
                hidden: false,
                index: index,
                datasetIndex: view.dbSet,
              });
              dataset.borderColor = this.arcPaddingColor.current;
              dataset.hoverBorderColor = this.arcPaddingColor.current;
            });
            return updatedLabels;
          },
          filter: (legendItem, chart) => {
            const chartData = this.doughnutHelper.getChartDataFromStatus(chart, legendItem.datasetIndex, legendItem.text as ReviewDocumentStatusType);
            if (chartData?.nested?.count > 0) {
              return true;
            }
            return false;
          },
        },
        onClick: null,
        onHover: (event, legendItem, legend) => {
          this.statusUpdate$.next(legendItem.text);
          const activeDatasets = [];
          this.doughnutChartData.datasets.forEach(ds => {
            const chartDataIndex = ds.data.findIndex(d => d.id === legendItem.text);
            const labelData = DocumentStatusToViewDataMap.get(legendItem.text as ReviewDocumentStatusType)
            if (chartDataIndex > -1)
              activeDatasets.push({ datasetIndex: labelData.dbSet, index: chartDataIndex });
            legend.chart.setActiveElements(
              activeDatasets,
            );
            legend.chart.update();
          })
        },
        onLeave: (event, legendItem: LegendItem, legend) => {
          legend.chart.setActiveElements([]);
          legend.chart.update();
        },
      },
      tooltip: null,
    },
  };

  public ngChartPlugins = [{
    id: 'chartIsReady',
    afterRender: () => {
      if (this.doughnutChartData.datasets.length > 0) {
        this.shouldShowLoader = false;
      }
    }
  }];

  public doughnutChartData = {
    datasets: [],
    labels: [],
  };
  public doughnutChartType: ChartType = 'doughnut';

  public doughnutHelper: DocReviewStatusDataHelper;

  private totalDocCount: number;
  private reviewedDocCount: number;
  private legendFontColor: string = 'rgb(102 102 102)';
  private arcPaddingColor = {lightTheme:  'rgb(255 255 255)', darkTheme: 'rgb(54 54 54)', current: 'rgb(255 255 255)'};
  private reviewDocumentState$ = new BehaviorSubject<ReviewDocumentState>(null);
  private statusUpdate$ = new BehaviorSubject<string>(null);

  constructor(
    protected lexbeThemeService: LexbeThemeService,
    protected chartThemeService: ChartThemeService) {
    super(lexbeThemeService, chartThemeService);
  }

  ngOnInit(): void {
    this.reviewDocumentState$.pipe(
      filter((response) => (!!response)),
      takeUntil(this.destroy$),
    ).subscribe((data) => {
      this.doughnutHelper = new DocReviewStatusDataHelper(DocumentStatusToViewDataMap, 'Responsiveness', 'Reviewed Status');
      this.doughnutChartData.labels = this.doughnutHelper.getAllSatuses();
      this.totalDocCount = data.totalDocCount;
      this.reviewedDocCount = data.reviewedDocCount;
      const remainingCount = data.totalDocCount - data.reviewedDocCount;
      const reviewedChartData: ReviewDocStatusChartData[] = [{
        id: 'Reviewed',
        nested: {
          count: this.reviewedDocCount,
          doughnutDisplayAmount: (this.reviewedDocCount / this.totalDocCount) * 100,
        }
      },
      {
        id: 'Not Reviewed',
        nested: {
          count: remainingCount,
          doughnutDisplayAmount: (remainingCount / this.totalDocCount) * 100,
        }
      }];

      const responsiveRingData = this.doughnutHelper.responsiveness.initializeEmptyDataset();
      const reviewedRingData = this.doughnutHelper.reviewed.initializeDataset(reviewedChartData);

      const sortedData = data.responsivenessStates.sort((a, b) => {
        const orderA = this.doughnutHelper.datamap.get(a.status)?.order ?? Number.MAX_VALUE;
        const orderB = this.doughnutHelper.datamap.get(b.status)?.order ?? Number.MAX_VALUE;
        return orderA - orderB;
      });
      sortedData.forEach(s => {
        const itemData: ReviewDocStatusChartData = {
          id: s.status,
          nested: {
            count: s.statusCount,
            doughnutDisplayAmount: (s.statusCount / this.totalDocCount) * 100,
          }
        };

        if (this.doughnutHelper.getViewDataFromStatus(s.status).dbSet === this.doughnutHelper.responsiveness.dbset) {
          const data = responsiveRingData.find(s => s.id === itemData.id);
          data.nested.count = itemData.nested.count;
          if (itemData.nested.doughnutDisplayAmount < 1 && itemData.nested.doughnutDisplayAmount > 0) {
            itemData.nested.doughnutDisplayAmount = 1 + itemData.nested.doughnutDisplayAmount;
          }
          data.nested.doughnutDisplayAmount = itemData.nested.doughnutDisplayAmount;
        }
      });
      const responsiveColors = responsiveRingData.map(d => this.getBaseColor(d.id));
      const responsiveHoverColors = responsiveRingData.map(d => this.getHoverColor(d.id));

      //We might still use this. Keep it commented for now
      //const borderColors = responsiveRingData.map(d => this.getBoarderColor(d.id));

      const borderColors = Array.from({ length: 4 }).map(() => this.arcPaddingColor.current);

      const reviewedColors = reviewedRingData.map(d => this.getBaseColor(d.id));
      const reviewedHoverColors = reviewedRingData.map(d => this.getHoverColor(d.id));
      //We might still use this. Keep it commented for now
      //const reviewedBorderColors = reviewedRingData.map(d => this.getBoarderColor(d.id));
      const reviewedBorderColors =  Array.from({ length: 2 }).map(() => this.arcPaddingColor.current);

      this.doughnutChartData.datasets = [
        {
          data: responsiveRingData,
          backgroundColor: responsiveColors,
          hoverBackgroundColor: responsiveHoverColors,
          hoverBorderColor: borderColors,
          label: 'Responsiveness',
          borderColor: borderColors,
          borderWidth: 1,
          hoverBorderWidth: 1,
          hoverOffset: 0,
          weight: 1,
          cutout: '30%',
          radius: '85%',
        },
        {
          data: reviewedRingData,
          backgroundColor: reviewedColors,
          hoverBackgroundColor: reviewedHoverColors,
          hoverBorderColor: reviewedBorderColors,
          hoverBorderWidth: 1,
          label: 'Reviewed Status',
          borderColor: reviewedBorderColors,
          borderWidth: 1,
          hoverOffset: 0,
          weight: 1,
          cutout: '30%',
          radius: '85%',
        }
      ];
    });

    this.statusUpdate$.subscribe(s => {
      this.statusView = this.updateStatusView(s as ReviewDocumentStatusType);
    });

    this.lexbeThemeService.isDarkMode$.subscribe((value) => {
      this.legendFontColor = value ? '#ffffff' : 'rgb(102 102 102)';
      this.arcPaddingColor.current = value ? this.arcPaddingColor.darkTheme : this.arcPaddingColor.lightTheme;
    });
  }

  private handleHover(event, chartElement): void {
    if (chartElement.length) {
      const element = chartElement[0];
      const datasetIndex = element.datasetIndex;
      const index = element.index;
      const labels = [];
      DocumentStatusToViewDataMap.forEach((value, key) => {
        if (value.dbSet === datasetIndex) {
          labels.push(key);
        }
      });
      const label = labels[index];

      this.statusUpdate$.next(label);
      event.chart.setActiveElements([{ datasetIndex: datasetIndex, index: index }]);

    }
    else {
      this.statusUpdate$.next(null);
      event.chart.setActiveElements([]);
    }

      event.chart.update();
  }

  private updateStatusView(label: ReviewDocumentStatusType): ReviewDocumentStatusView {
    if (!label || this.doughnutHelper.getIndexFromStatus(label) === -1)
      return null;

    const labelData = this.doughnutHelper.datamap.get(label);
    const dataItem = this.doughnutHelper.getChartDataFromStatus(this.doughnutChartData, labelData.dbSet, label);
    if (!dataItem)
      return null;

    const count = dataItem.nested.count;
    let percentage = 0;
    percentage = (count / this.totalDocCount) * 100;

    return {
      dbSet: labelData.dbSet === this.doughnutHelper.responsiveness.dbset ? 'Responsiveness' : 'Reviewed Status',
      status: label,
      statusCount: count,
      statusPercentage: percentage
    }
  }

  private getBaseColor(status: ReviewDocumentStatusType): string {
    const label = cloneDeep(DocumentStatusToViewDataMap.get(status));
    return `rgba(${label.colorArray[0]}, ${label.colorArray[1]}, ${label.colorArray[2]})`;
  }

  private getBoarderColor(status: ReviewDocumentStatusType): string {
    const label = cloneDeep(DocumentStatusToViewDataMap.get(status));
    return `rgb(${label.colorArray[0]}, ${label.colorArray[1]}, ${label.colorArray[2]})`;
  }

  private getHoverColor(status: ReviewDocumentStatusType): string {
    const label = cloneDeep(DocumentStatusToViewDataMap.get(status));
    const hoverBrightness = 40;
    const hoverRgb = label.colorArray.map(value => value + hoverBrightness <= 255 ? value + hoverBrightness : 255);
    return `rgb(${hoverRgb[0]}, ${hoverRgb[1]}, ${hoverRgb[2]})`;
  }
}

export const DocumentStatusToViewDataMap: Map<ReviewDocumentStatusType, ReviewDocumentViewData> = new Map([
  ['Responsive', { dbSet: 0, order: 1, colorArray: [42, 157, 143]}],
  ['Non Responsive', { dbSet: 0, order: 2, colorArray: [216, 30, 91] }],
  ['Not Specified', { dbSet: 0, order: 3, colorArray: [110, 119, 144] }],
  ['Needs Further Review', { dbSet: 0, order: 4, colorArray:[255, 190, 11] }],
  ['Reviewed', { dbSet: 1, order: 5, colorArray: [42, 157, 143] }],
  ['Not Reviewed', { dbSet: 1, order: 6, colorArray:  [36, 62, 144]}]
]);

export class DocReviewStatusDataHelper {
  readonly datamap: Map<ReviewDocumentStatusType, ReviewDocumentViewData>;
  readonly responsiveness: DbsetLookup;
  readonly reviewed: DbsetLookup;
  constructor(datamap: Map<ReviewDocumentStatusType, ReviewDocumentViewData>,
    responsivenessSetLabel: ReviewDocumentStatusCategory,
    reviewedSetLabel: ReviewDocumentStatusCategory
    ){
      this.datamap = datamap;
      this.responsiveness = new DbsetLookup(responsivenessSetLabel, this.datamap.get('Responsive').dbSet);
      this.reviewed = new DbsetLookup(reviewedSetLabel, this.datamap.get('Reviewed').dbSet);
  }

  getAllSatuses(): string[] {
    return Array.from(this.datamap.keys());
  }

  getViewDataFromStatus(status: ReviewDocumentStatusType): ReviewDocumentViewData {
    return cloneDeep(this.datamap.get(status));
  }

  getChartDataFromStatus(doughnutChart: any, dbset: number, status: ReviewDocumentStatusType): ReviewDocStatusChartData {
    return doughnutChart.datasets[dbset].data.find(d => d.id === status);
  }

  getChartDataFromIndex(doughnutChart: any, dbset: number, index: number): ReviewDocStatusChartData {
    return doughnutChart.datasets[dbset].data[index];
  }

  getIndexFromStatus(lookupStatus: ReviewDocumentStatusType): number {
    let statuses = this.responsiveness.getStatuses();
    const index = statuses.findIndex(status => status === lookupStatus);
    if (index > -1) {
        return index;
    }

    statuses = this.reviewed.getStatuses()
    return statuses.findIndex(status => status === lookupStatus);
  }
}

export class DbsetLookup {
  label: ReviewDocumentStatusCategory;
  dbset: number;
  constructor(label: ReviewDocumentStatusCategory, dbSet: number) {
    this.label = label;
    this.dbset = dbSet;
  }
  getFilteredData() {
    return Array.from(DocumentStatusToViewDataMap.entries()).filter(([, view],) => view.dbSet === this.dbset);
  }
  getStatuses() {
    return Array.from(this.getFilteredData().values()).map(([key,],) => key);
  }
  initializeEmptyDataset(): ReviewDocStatusChartData[] {
    return this.getFilteredData().map(([status,],) => {
      return { id: status, nested: { count: 0, doughnutDisplayAmount: 0 } };
    });
  }
  initializeDataset(initialData: ReviewDocStatusChartData[]): ReviewDocStatusChartData[] {
    const declaredData = this.getFilteredData().map(([status,],) => {
      return { id: status, nested: { count: 0, doughnutDisplayAmount: 0 } }
    });
    initialData.forEach(item => {
      const data = declaredData.find(data => data.id === item.id);
      data.nested.count = item.nested.count;

      if (item.nested.doughnutDisplayAmount < 1 && item.nested.doughnutDisplayAmount > 0) {
        item.nested.doughnutDisplayAmount = 1 + item.nested.doughnutDisplayAmount;
      }
      data.nested.doughnutDisplayAmount = item.nested.doughnutDisplayAmount;
    });

    return declaredData;
  }
}


export class ReviewDocumentStatusView {
  dbSet: string;
  status: ReviewDocumentStatusType;
  statusCount: number;
  statusPercentage: number;
}

export class ReviewDocumentViewData {
  dbSet: number;
  order: number;
  colorArray: number[];
}

interface ReviewDocStatusChartData {
  id: ReviewDocumentStatusType,
  nested: {
    count: number,
    doughnutDisplayAmount: number;
  }
}
